import React, { Component } from "react";
import { Form, FormikProps, withFormik } from "formik";
import * as Yup from "yup";

import ActionFormButton from "../../../common/ActionFormButton";
import {
  getRowItemObject,
  renderRowWithSingleItem,
  renderRowWithTwoItems,
  renderSectionTitle,
  Section,
  Wrapper,
} from "../../../common/FormUtils";
import {
  normalizeQuestions,
  Questionnaire,
  TemplateGoal,
  QuestionType,
} from "../../../../types/Questionnaire";
import { Plan } from "../../../../types/Plan";
import Button from "@material-ui/core/Button";
import {
  AI_PROMPT_SHARED_TAIL,
  generateQuestionnaireSuggestion,
  ContentSources,
} from "../../onboarding/OnboardingAPI";
import {
  buildLearningPlacementRouting,
  LEARNING_PLACEMENT_LEVELS,
  LearningPlacementFormValues,
  mergeQuestionnaireWithLearningPlacement,
  validateLearningPlacementSelection,
} from "./learningPlacementForm";
const QUESTION_TYPE_OPTIONS = [
  { label: "Multi select", value: QuestionType.MULTIPLE_SELECTION },
  { label: "Number", value: QuestionType.NUMBER },
  { label: "Text", value: QuestionType.TEXT },
  { label: "Single select", value: QuestionType.SINGLE_SELECTION },
];

const looseJsonParse = (text: string): any => {
  const tryParse = (payload: string) => JSON.parse(payload);

  try {
    return tryParse(text);
  } catch (err) {
    // Attempt to escape stray quotes inside string values
    let fixed = "";
    let inString = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (!inString && char === '"') {
        inString = true;
        fixed += char;
        continue;
      }

      if (inString && char === '"' && text[i - 1] !== "\\") {
        // look ahead for next non-space char to guess if this is a closing quote
        let j = i + 1;
        while (j < text.length && /\s/.test(text[j])) j++;
        const nextChar = text[j];
        const looksLikeEnd =
          nextChar === "," ||
          nextChar === "}" ||
          nextChar === "]" ||
          nextChar === ":";
        if (looksLikeEnd) {
          inString = false;
          fixed += char;
        } else {
          fixed += '\\"';
        }
        continue;
      }

      fixed += char;
    }

    return tryParse(fixed);
  }
};

const parseWithFallback = (raw: string): any => {
  // Normalize smart quotes
  const normalizeQuotes = (input: string) =>
    input.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

  const cleaned = normalizeQuotes(raw);

  try {
    return looseJsonParse(cleaned);
  } catch (err) {
    const escapeInnerQuotesForKey = (text: string, key: string) => {
      const pattern = new RegExp(`"${key}"\\s*:\\s*"`, "m");
      const match = text.match(pattern);
      if (!match || match.index === undefined) return text;

      const start = match.index + match[0].length;
      let value = "";
      let i = start;

      for (; i < text.length; i++) {
        const char = text[i];
        if (char === '"' && text[i - 1] !== "\\") {
          let j = i + 1;
          while (j < text.length && /\\s/.test(text[j])) j++;
          const nextChar = text[j];
          const looksLikeEnd =
            nextChar === "," ||
            nextChar === "}" ||
            nextChar === "]" ||
            nextChar === undefined;
          if (looksLikeEnd) {
            break;
          }
          value += '\\"';
          continue;
        }
        value += char;
      }

      const escaped = text.slice(0, start) + value + text.slice(i);
      return escaped;
    };

    const fixedPrompt = escapeInnerQuotesForKey(cleaned, "ai_prompt");

    try {
      return looseJsonParse(fixedPrompt);
    } catch (err2) {
      // As a last resort, try parsing as a JS object literal (still only if it looks like an object/array)
      if (/^\\s*[{\\[]/.test(fixedPrompt.trim())) {
        // eslint-disable-next-line no-new-func
        const fn = new Function(`return (${fixedPrompt});`);
        return fn();
      }
      throw err2;
    }
  }
};

type OuterProps = {
  onChageFormToEditMode: () => void;
  onRemoveItem: () => void;
  onCreateItem: (questionnaire: Questionnaire) => void | Promise<void>;
  onEditItem: (questionnaire: Questionnaire) => void | Promise<void>;
  onUploadItem?: (questionnaire: Questionnaire) => void | Promise<void>;
  item: Questionnaire;
  plans?: Plan[];
  plansLoading?: boolean;
  plansError?: boolean;
  mode: string;
  isActive?: boolean;
};

type FormValues = LearningPlacementFormValues & {
  app: string;
  version: string;
  categories: string;
  ai_prompt: string;
  loadingTitle: string;
  loadingProgressLabel: string;
  loadingAlerts: string;
  questionsJson: string;
  interstitialsJson: string;
  beforeAfterBefore: string;
  beforeAfterAfter: string;
  commitGreeting: string;
  commitCommitments: string;
  commitSubcopy: string;
  commitNeedSignature: string;
  commitPrimaryCta: string;
  reviewsJson: string;
  readyHeadline: string;
  readyBullets: string;
  readyPrimaryCta: string;
  importJsonText: string;
  templateGoalsJson: string;
  sourceQuestionnaire: Questionnaire;
};

type Props = FormikProps<FormValues> & OuterProps;

type State = {
  importError: string | null;
  showImportForm: boolean;
  templateGoalsError: string | null;
  showQuestionsSection: boolean;
  showInterstitialsSection: boolean;
  showTemplateGoalsSection: boolean;
  expandedItems: { [key: string]: boolean };
  isGeneratingSuggestion: boolean;
  suggestionError: string | null;
};

class OnboardingForm extends Component<Props, State> {
  state: State = {
    importError: null,
    showImportForm: false,
    templateGoalsError: null,
    showQuestionsSection: true,
    showInterstitialsSection: true,
    showTemplateGoalsSection: true,
    expandedItems: {},
    isGeneratingSuggestion: false,
    suggestionError: null,
  };

  handleAISuggest = async () => {
    const { values, setValues } = this.props;
    const context = [
      values.ai_prompt,
      values.app ? `Community: ${values.app}` : "",
      values.categories ? `Categories: ${values.categories}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    this.setState({ isGeneratingSuggestion: true, suggestionError: null });
    try {
      const sources: ContentSources = { pastedContent: context || undefined };
      const suggestion = await generateQuestionnaireSuggestion(sources);

      setValues({
        ...values,
        // Only fill in empty fields
        ai_prompt: values.ai_prompt || suggestion.aiPrompt,
        questionsJson:
          values.questionsJson ||
          (suggestion.questions.length
            ? JSON.stringify(suggestion.questions, null, 2)
            : ""),
        beforeAfterBefore:
          values.beforeAfterBefore || suggestion.beforeAfter.before.join("\n"),
        beforeAfterAfter:
          values.beforeAfterAfter || suggestion.beforeAfter.after.join("\n"),
        commitGreeting: values.commitGreeting || suggestion.commit.greeting,
        commitCommitments:
          values.commitCommitments ||
          suggestion.commit.commitments.join("\n"),
        commitSubcopy: values.commitSubcopy || suggestion.commit.subcopy,
        commitPrimaryCta:
          values.commitPrimaryCta || suggestion.commitPrimaryCta,
        reviewsJson:
          values.reviewsJson ||
          (suggestion.reviews.length
            ? JSON.stringify(suggestion.reviews, null, 2)
            : ""),
        loadingTitle: values.loadingTitle || suggestion.loadingTitle,
        loadingProgressLabel:
          values.loadingProgressLabel || suggestion.loadingProgressLabel,
        loadingAlerts:
          values.loadingAlerts || suggestion.loadingAlerts.join("\n"),
        readyHeadline: values.readyHeadline || suggestion.readyHeadline,
        readyBullets:
          values.readyBullets || suggestion.readyBullets.join("\n"),
        readyPrimaryCta: values.readyPrimaryCta || suggestion.readyPrimaryCta,
      });
      this.setState({ isGeneratingSuggestion: false });
    } catch (error) {
      this.setState({
        isGeneratingSuggestion: false,
        suggestionError:
          "AI suggestion failed. " + (error as Error)?.message,
      });
    }
  };

  handleApplyJsonText = () => {
    const { setValues, values } = this.props;
    if (!values?.importJsonText) return;

    try {
      const data = parseWithFallback(values.importJsonText) as Questionnaire;
      const initialValues = questionnaireToValues(data);
      setValues({
        ...values,
        ...initialValues,
        importJsonText: values.importJsonText,
      });
      this.setState({ importError: null });
    } catch (error) {
      console.error("Invalid JSON provided", error);
      this.setState({
        importError:
          "Invalid JSON. Please check the format. " + (error as Error)?.message,
      });
    }
  };

  handleUploadItem = () => {
    const { onUploadItem, values, setStatus } = this.props;
    if (!onUploadItem) return;

    const placementError = validateLearningPlacementSelection({
      values,
      plans: this.props.plans || [],
      plansLoading: this.props.plansLoading,
      plansError: this.props.plansError,
    });
    if (placementError) {
      setStatus?.({ learningPlacementError: placementError });
      return;
    }

    const templateGoals = this.parseTemplateGoalsOrSetError();
    if (templateGoals === null) return;

    setStatus?.({
      learningPlacementError: null,
      templateGoalsError: null,
    });
    const questionnaire = buildQuestionnaireFromValues(values, templateGoals);
    onUploadItem(questionnaire);
  };

  toggleImportForm = () => {
    this.setState((prevState) => ({
      showImportForm: !prevState.showImportForm,
      importError: null,
    }));
  };

  parseTemplateGoalsOrSetError = (): TemplateGoal[] | null => {
    const raw = this.props.values?.templateGoalsJson || "";

    try {
      const parsed = parseTemplateGoals(raw);
      this.setState({ templateGoalsError: null });
      this.props.setStatus?.({ templateGoalsError: null });
      return parsed;
    } catch (error) {
      this.setState({
        templateGoalsError:
          "Invalid Template Goals. Please provide valid JSON or object literal. " +
          (error as Error)?.message,
      });
      this.props.setStatus?.({
        templateGoalsError:
          "Invalid Template Goals. Please provide valid JSON or object literal. " +
          (error as Error)?.message,
      });
      return null;
    }
  };

  toggleQuestionsSection = () => {
    this.setState((prevState) => ({
      showQuestionsSection: !prevState.showQuestionsSection,
    }));
  };

  toggleInterstitialsSection = () => {
    this.setState((prevState) => ({
      showInterstitialsSection: !prevState.showInterstitialsSection,
    }));
  };

  toggleTemplateGoalsSection = () => {
    this.setState((prevState) => ({
      showTemplateGoalsSection: !prevState.showTemplateGoalsSection,
    }));
  };

  toggleItemExpanded = (fieldId: string, index: number) => {
    const key = `${fieldId}-${index}`;
    this.setState((prevState) => ({
      expandedItems: {
        ...prevState.expandedItems,
        [key]: !prevState.expandedItems[key],
      },
    }));
  };

  renderJsonArrayEditor = (label: string, fieldId: string, isCollapsed?: boolean, onToggle?: () => void) => {
    const { values, setFieldValue, mode } = this.props;
    const parsed = parseJSONField(values[fieldId], []);
    const items = Array.isArray(parsed) ? parsed : [];

    const updateItems = (nextItems: any[]) => {
      setFieldValue(fieldId, JSON.stringify(nextItems, null, 2));
    };

    const onChangeProp = (
      index: number,
      key: string,
      value: string | number | boolean
    ) => {
      const nextItems = [...items];
      nextItems[index] = { ...nextItems[index], [key]: value };
      updateItems(nextItems);
    };

    const onChangeNestedArrayProp = (
      itemIndex: number,
      arrayKey: string,
      optionIndex: number,
      propKey: string,
      value: string
    ) => {
      const nextItems = [...items];
      const current = nextItems[itemIndex] || {};
      const currentArray = Array.isArray(current[arrayKey])
        ? [...current[arrayKey]]
        : [];
      currentArray[optionIndex] = {
        ...(currentArray[optionIndex] || {}),
        [propKey]: value,
      };
      nextItems[itemIndex] = { ...current, [arrayKey]: currentArray };
      updateItems(nextItems);
    };

    const onAddNestedArrayItem = (itemIndex: number, arrayKey: string) => {
      const nextItems = [...items];
      const current = nextItems[itemIndex] || {};
      const currentArray = Array.isArray(current[arrayKey])
        ? [...current[arrayKey]]
        : [];
      currentArray.push({});
      nextItems[itemIndex] = { ...current, [arrayKey]: currentArray };
      updateItems(nextItems);
    };

    const onRemoveNestedArrayItem = (
      itemIndex: number,
      arrayKey: string,
      optionIndex: number
    ) => {
      const nextItems = [...items];
      const current = nextItems[itemIndex] || {};
      const currentArray = Array.isArray(current[arrayKey])
        ? [...current[arrayKey]]
        : [];
      currentArray.splice(optionIndex, 1);
      nextItems[itemIndex] = { ...current, [arrayKey]: currentArray };
      updateItems(nextItems);
    };

    const onAddItem = () => updateItems([...items, {}]);
    const onRemoveItem = (index: number) =>
      updateItems(items.filter((_, i) => i !== index));

    const onAddProp = (index: number) => {
      const propName = `field_${Object.keys(items[index] || {}).length + 1}`;
      onChangeProp(index, propName, "");
    };

    const onRemoveProp = (index: number, key: string) => {
      const nextItems = [...items];
      if (!nextItems[index]) return;
      const { [key]: _, ...rest } = nextItems[index];
      nextItems[index] = rest;
      updateItems(nextItems);
    };

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontWeight: 600, margin: 0 }}>{label}</p>
          {onToggle && (
            <Button
              variant="text"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                onToggle();
              }}
              style={{ minWidth: 'auto', padding: '4px 8px' }}
            >
              {isCollapsed ? '▼ Show' : '▲ Hide'}
            </Button>
          )}
        </div>
        {!isCollapsed && items.map((item, index) => {
          const itemKey = `${fieldId}-${index}`;
          const isExpanded = this.state.expandedItems[itemKey] || false;
          const titleKey = item?.title || item?.text || item?.question || `Item ${index + 1}`;

          return (
            <div
              key={itemKey}
              style={{
                marginBottom: 12,
                border: "1px solid #ccc",
                padding: 8,
                borderRadius: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isExpanded ? 8 : 0 }}>
                <div style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>
                  {titleKey}
                </div>
                <Button
                  variant="text"
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    this.toggleItemExpanded(fieldId, index);
                  }}
                  style={{ minWidth: 'auto', padding: '4px 8px' }}
                >
                  {isExpanded ? '▲' : '▼'}
                </Button>
              </div>
              {isExpanded && (
                <>
                  {[
                    ...Object.keys(item || {}).filter((k) => k !== "options"),
                    ...Object.keys(item || {}).filter((k) => k === "options"),
                  ].map((key) => (
                    <div key={`${fieldId}-${index}-${key}`} style={{ marginBottom: 6 }}>
                      <label style={{ display: "block", fontSize: 12, color: "#666" }}>
                        {key}
                      </label>
                      {Array.isArray(item[key]) ? (
                        <div
                          style={{
                            border: "1px solid #eee",
                            padding: 8,
                            borderRadius: 4,
                            marginLeft: 12,
                          }}
                        >
                          {(item[key] as any[]).map((opt, optIdx) => (
                            <div
                              key={`${fieldId}-${index}-${key}-${optIdx}`}
                              style={{
                                marginBottom: 8,
                                paddingLeft: 8,
                                borderLeft: "2px solid #ddd",
                              }}
                            >
                              <p style={{ fontSize: 12, margin: 0, color: "#888" }}>
                                Option #{optIdx + 1}
                              </p>
                              {Object.keys(opt || {}).map((propKey) => (
                                <div
                                  key={`${fieldId}-${index}-${key}-${optIdx}-${propKey}`}
                                  style={{ marginBottom: 4 }}
                                >
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: 11,
                                      color: "#777",
                                    }}
                                  >
                                    {propKey}
                                  </label>
                                  <input
                                    style={{ width: "100%", padding: 6 }}
                                    value={opt[propKey] ?? ""}
                                    disabled={mode === "detail"}
                                    onChange={(e) =>
                                      onChangeNestedArrayProp(
                                        index,
                                        key,
                                        optIdx,
                                        propKey,
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              ))}
                              {mode !== "detail" && (
                                <Button
                                  variant="text"
                                  color="secondary"
                                  onClick={() => onRemoveNestedArrayItem(index, key, optIdx)}
                                >
                                  Remove option
                                </Button>
                              )}
                            </div>
                          ))}
                          {mode !== "detail" && (
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => onAddNestedArrayItem(index, key)}
                            >
                              Add option
                            </Button>
                          )}
                        </div>
                      ) : (
                        <>
                          {key === "type" ? (
                            <select
                              style={{ width: "100%", padding: 6 }}
                              value={item[key] ?? ""}
                              disabled={mode === "detail"}
                              onChange={(e) =>
                                onChangeProp(
                                  index,
                                  key,
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value)
                                )
                              }
                            >
                              <option value="">Select a type</option>
                              {QUESTION_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              style={{ width: "100%", padding: 6 }}
                              value={item[key] ?? ""}
                              disabled={mode === "detail"}
                              onChange={(e) => onChangeProp(index, key, e.target.value)}
                            />
                          )}
                          {mode !== "detail" && (
                            <Button
                              variant="text"
                              color="secondary"
                              onClick={() => onRemoveProp(index, key)}
                            >
                              Remove field
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                  {mode !== "detail" && (
                    <>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => onAddProp(index)}
                        style={{ marginRight: 8 }}
                      >
                        Add field
                      </Button>
                      <Button
                        variant="text"
                        color="secondary"
                        onClick={() => onRemoveItem(index)}
                      >
                        Remove item
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
        {!isCollapsed && mode !== "detail" && (
          <Button variant="outlined" color="primary" onClick={onAddItem}>
            Add {label.slice(0, -1)}
          </Button>
        )}
      </div>
    );
  };

  renderImportSection = () => {
    const importJsonField = getRowItemObject(
      "Import JSON",
      "Paste questionnaire JSON here",
      "textarea",
      "importJsonText"
    );

    return (
      <Section>
        {renderRowWithSingleItem(importJsonField, this.props)}
        <Button
          variant="outlined"
          color="primary"
          onClick={(event) => {
            event.preventDefault();
            this.handleApplyJsonText();
          }}
        >
          Apply JSON
        </Button>
        {this.state.importError && (
          <p style={{ color: "red", marginTop: 8 }}>{this.state.importError}</p>
        )}
      </Section>
    );
  };

  renderImportToggle = () => (
    <Button
      variant="outlined"
      color="primary"
      onClick={(event) => {
        event.preventDefault();
        this.toggleImportForm();
      }}
      style={{ marginBottom: 12 }}
    >
      {this.state.showImportForm ? "Cancel" : "Import JSON"}
    </Button>
  );

  renderAppSection = () => {
    const appInput = getRowItemObject("App", "App name", "text", "app");
    const versionInput = getRowItemObject(
      "Version",
      "Version (e.g. v1)",
      "text",
      "version"
    );
    const categoriesInput = getRowItemObject(
      "Categories",
      "Comma separated categories",
      "text",
      "categories"
    );
    const aiPromptInput = getRowItemObject(
      "AI Prompt",
      "Prompt used for AI generation",
      "textarea",
      "ai_prompt"
    );

    const { isGeneratingSuggestion, suggestionError } = this.state;
    const { mode } = this.props;

    return (
      <Section>
        {renderSectionTitle("App")}
        {renderRowWithTwoItems(appInput, versionInput, this.props)}
        {renderRowWithSingleItem(categoriesInput, this.props)}
        {renderRowWithSingleItem(aiPromptInput, this.props)}
        {mode !== "detail" && (
          <div style={{ marginTop: 8 }}>
            <Button
              variant="outlined"
              color="primary"
              disabled={isGeneratingSuggestion}
              onClick={(e) => {
                e.preventDefault();
                this.handleAISuggest();
              }}
            >
              {isGeneratingSuggestion ? "Generating..." : "AI Suggest missing fields"}
            </Button>
            {suggestionError && (
              <p style={{ color: "red", marginTop: 8 }}>{suggestionError}</p>
            )}
          </div>
        )}
      </Section>
    );
  };

  renderLoadingSection = () => {
    const loadingTitle = getRowItemObject(
      "Loading title",
      "Loading headline",
      "text",
      "loadingTitle"
    );
    const loadingProgress = getRowItemObject(
      "Progress label",
      "Progress text",
      "text",
      "loadingProgressLabel"
    );
    const loadingAlerts = getRowItemObject(
      "Loading alerts",
      "One per line",
      "textarea",
      "loadingAlerts"
    );

    return (
      <Section>
        {renderSectionTitle("Loading")}
        {renderRowWithTwoItems(loadingTitle, loadingProgress, this.props)}
        {renderRowWithSingleItem(loadingAlerts, this.props)}
      </Section>
    );
  };

  renderQuestionsSection = () => {
    return (
      <Section>
        {renderSectionTitle("Questions & Interstitials")}
        {this.renderJsonArrayEditor(
          "Questions",
          "questionsJson",
          !this.state.showQuestionsSection,
          this.toggleQuestionsSection
        )}
        {this.renderJsonArrayEditor(
          "Interstitials",
          "interstitialsJson",
          !this.state.showInterstitialsSection,
          this.toggleInterstitialsSection
        )}
      </Section>
    );
  };

  renderBeforeAfterSection = () => {
    const beforeInput = getRowItemObject(
      "Before",
      "One per line",
      "textarea",
      "beforeAfterBefore"
    );
    const afterInput = getRowItemObject(
      "After",
      "One per line",
      "textarea",
      "beforeAfterAfter"
    );

    return (
      <Section>
        {renderSectionTitle("Before / After")}
        {renderRowWithTwoItems(beforeInput, afterInput, this.props)}
      </Section>
    );
  };

  renderCommitSection = () => {
    const commitGreeting = getRowItemObject(
      "Commit greeting",
      "Greeting text",
      "text",
      "commitGreeting"
    );
    const commitSubcopy = getRowItemObject(
      "Commit subcopy",
      "Short description",
      "text",
      "commitSubcopy"
    );
    const commitCommitments = getRowItemObject(
      "Commitments",
      "One per line",
      "textarea",
      "commitCommitments"
    );
    const commitPrimaryCta = getRowItemObject(
      "Primary CTA",
      "CTA label",
      "text",
      "commitPrimaryCta"
    );
    const commitNeedSignature = getRowItemObject(
      "Need signature",
      "true / false",
      "text",
      "commitNeedSignature"
    );

    return (
      <Section>
        {renderSectionTitle("Commit")}
        {renderRowWithTwoItems(commitGreeting, commitSubcopy, this.props)}
        {renderRowWithSingleItem(commitCommitments, this.props)}
        {renderRowWithTwoItems(
          commitPrimaryCta,
          commitNeedSignature,
          this.props
        )}
      </Section>
    );
  };

  renderReviewsSection = () => {
    return (
      <Section>
        {renderSectionTitle("Reviews")}
        {this.renderJsonArrayEditor("Reviews", "reviewsJson")}
      </Section>
    );
  };

  renderReadySection = () => {
    const readyHeadline = getRowItemObject(
      "Ready headline",
      "Ready headline",
      "text",
      "readyHeadline"
    );
    const readyBullets = getRowItemObject(
      "Ready bullets",
      "One per line",
      "textarea",
      "readyBullets"
    );
    const readyPrimaryCta = getRowItemObject(
      "Ready primary CTA",
      "CTA label",
      "text",
      "readyPrimaryCta"
    );

    return (
      <Section>
        {renderSectionTitle("Ready")}
        {renderRowWithSingleItem(readyHeadline, this.props)}
        {renderRowWithSingleItem(readyBullets, this.props)}
        {renderRowWithSingleItem(readyPrimaryCta, this.props)}
      </Section>
    );
  };

  renderLearningPlacementSection = () => {
    const {
      mode,
      plans = [],
      plansLoading,
      plansError,
      setFieldValue,
      status,
      values,
    } = this.props;
    const routing = buildLearningPlacementRouting(values);
    const mappedCount = Object.keys(routing).length;
    const plansById = new Map(plans.map((plan) => [plan.id, plan]));
    const sortedPlans = plans
      .filter(
        (plan) =>
          plan.categoryId !== "workout" && plan.planType !== "exercise"
      )
      .sort((a, b) => {
        if ((a.isActive === false) !== (b.isActive === false)) {
          return a.isActive === false ? 1 : -1;
        }
        return (
          (a.position ?? Number.MAX_SAFE_INTEGER) -
            (b.position ?? Number.MAX_SAFE_INTEGER) ||
          String(a.title || "").localeCompare(String(b.title || ""))
        );
      });
    const placementError =
      (status as any)?.learningPlacementError || null;

    return (
      <Section>
        {renderSectionTitle("AI Learning Placement")}
        <p style={{ marginTop: 0, color: "#555", lineHeight: 1.5 }}>
          The onboarding insight recommends a level from the member's full
          answer set. It can route only to the plans selected here. Leave all
          three empty to keep the current onboarding path unchanged.
        </p>
        <p
          style={{
            color: mappedCount === 3 ? "#287a45" : "#8a5a00",
            fontWeight: 600,
          }}
        >
          {mappedCount === 3
            ? "Ready for a three-level pilot"
            : `${mappedCount} of 3 levels mapped`}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {LEARNING_PLACEMENT_LEVELS.map(
            ({ key, label, description }) => {
              const fieldName = `${key}PlanId` as keyof FormValues;
              const value = String(values[fieldName] || "");
              const selectedPlanIsMissing =
                Boolean(value) && !plansById.has(value);

              return (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  <span style={{ color: "#666", fontSize: 12 }}>
                    {description}
                  </span>
                  <select
                    value={value}
                    disabled={mode === "detail" || Boolean(plansLoading)}
                    onChange={(event) =>
                      setFieldValue(fieldName, event.target.value)
                    }
                    style={{ minHeight: 40, padding: "8px 10px" }}
                  >
                    <option value="">
                      {plansLoading ? "Loading plans..." : "No route"}
                    </option>
                    {selectedPlanIsMissing ? (
                      <option value={value} disabled>
                        Missing plan ({value})
                      </option>
                    ) : null}
                    {sortedPlans.map((plan) => (
                      <option
                        key={plan.id}
                        value={plan.id}
                        disabled={plan.isActive === false}
                      >
                        {plan.title || plan.id}
                        {plan.level ? ` · ${String(plan.level)}` : ""}
                        {plan.isActive === false ? " · Inactive" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              );
            }
          )}
        </div>
        {plansError ? (
          <p style={{ color: "#b00020", marginTop: 10 }}>
            Learning plans could not be loaded. Existing placement is
            preserved until this is resolved.
          </p>
        ) : null}
        {placementError ? (
          <p style={{ color: "#b00020", marginTop: 10 }}>
            {placementError}
          </p>
        ) : null}
        <p style={{ color: "#666", fontSize: 12, marginBottom: 0 }}>
          Use three distinct, active plans with genuinely different assumed
          knowledge. Low-confidence AI results fall back to deterministic
          answer routing and then the normal front-door plan.
        </p>
      </Section>
    );
  };

  renderTemplateGoalsSection = () => {
    const templateGoalsInput = getRowItemObject(
      "Template goals",
      "Paste a JSON array or object representing template goals",
      "textarea",
      "templateGoalsJson"
    );

    const templateGoalsError =
      this.state.templateGoalsError ||
      ((this.props.status as any)?.templateGoalsError ?? null);

    return (
      <Section>
        {renderSectionTitle("Template Goals")}
        {renderRowWithSingleItem(templateGoalsInput, this.props)}
        {this.renderJsonArrayEditor(
          "Template Goals",
          "templateGoalsJson",
          !this.state.showTemplateGoalsSection,
          this.toggleTemplateGoalsSection
        )}
        {templateGoalsError ? (
          <p style={{ color: "red", marginTop: 8 }}>{templateGoalsError}</p>
        ) : null}
      </Section>
    );
  };

  render() {
    const {
      onChageFormToEditMode,
      onRemoveItem,
      isSubmitting,
      mode,
      onUploadItem,
      isActive,
    } = this.props;

    return (
      <Wrapper>
        <Form>
          {this.renderImportToggle()}
          {this.state.showImportForm && this.renderImportSection()}
          {this.renderAppSection()}
          {this.renderLoadingSection()}
          {this.renderQuestionsSection()}
          {this.renderBeforeAfterSection()}
          {this.renderCommitSection()}
          {this.renderReviewsSection()}
          {this.renderReadySection()}
          {this.renderLearningPlacementSection()}
          {this.renderTemplateGoalsSection()}
          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={this.props.handleSubmit}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Onboarding"
            canBeRemoved
            mode={mode}
            hidden={!isActive}
          />
          {onUploadItem && (
            <Button
              style={{ marginTop: 12 }}
              variant="contained"
              color="primary"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                this.handleUploadItem();
              }}
              disabled={isSubmitting}
            >
              Save to Questionnaire
            </Button>
          )}
        </Form>
      </Wrapper>
    );
  }
}

const validationSchema = Yup.object().shape({
  app: Yup.string().required("App is required"),
  version: Yup.string().required("Version is required"),
});

const questionnaireToValues = (item: Questionnaire): FormValues => ({
  app: item?.app || "",
  version: item?.version || "",
  categories: item?.categories?.join(", ") || "",
  ai_prompt: (item?.ai_prompt || "").replace(AI_PROMPT_SHARED_TAIL.trim(), "").trim(),
  loadingTitle: item?.loading?.title || "",
  loadingProgressLabel: item?.loading?.progress_label || "",
  loadingAlerts: item?.loading?.alerts?.join("\n") || "",
  questionsJson: item?.questions
    ? JSON.stringify(normalizeQuestions(item.questions as any[]), null, 2)
    : "",
  interstitialsJson: item?.interstitials
    ? JSON.stringify(item.interstitials, null, 2)
    : "",
  beforeAfterBefore: item?.beforeAfter?.before?.join("\n") || "",
  beforeAfterAfter: item?.beforeAfter?.after?.join("\n") || "",
  commitGreeting: item?.commit?.greeting || "",
  commitCommitments: item?.commit?.commitments?.join("\n") || "",
  commitSubcopy: item?.commit?.subcopy || "",
  commitNeedSignature:
    item?.commit?.needSignature !== undefined
      ? String(item.commit.needSignature)
      : "",
  commitPrimaryCta: item?.commit?.primaryCta || "",
  reviewsJson: item?.reviews ? JSON.stringify(item.reviews, null, 2) : "",
  readyHeadline: item?.ready?.headline || "",
  readyBullets: item?.ready?.bullets?.join("\n") || "",
  readyPrimaryCta: item?.ready?.primaryCta || "",
  importJsonText: "",
  templateGoalsJson: item?.templateGoals
    ? JSON.stringify(item.templateGoals, null, 2)
    : "",
  beginnerPlanId: item?.learningPlacementRouting?.beginner || "",
  intermediatePlanId: item?.learningPlacementRouting?.intermediate || "",
  advancedPlanId: item?.learningPlacementRouting?.advanced || "",
  sourceQuestionnaire: item || ({} as Questionnaire),
});

const parseList = (input: string, separator: string): string[] =>
  input
    ?.split(separator)
    .map((item) => item.trim())
    .filter(Boolean) || [];

const parseJSONField = <T,>(input: string, fallback: T): T => {
  if (!input) return fallback;
  try {
    return JSON.parse(input) as T;
  } catch (error) {
    console.warn("Invalid JSON provided. Falling back to default.", error);
    return fallback;
  }
};

const normalizeGoalTypeTokens = (input: string): string =>
  input
    .replace(/GoalType\.LONG_TERM/gi, '"long_term"')
    .replace(/GoalType\.MID_TERM/gi, '"mid_term"')
    .replace(/GoalType\.SHORT_TERM/gi, '"short_term"')
    .replace(/"LONG_TERM"/gi, '"long_term"')
    .replace(/"MID_TERM"/gi, '"mid_term"')
    .replace(/"SHORT_TERM"/gi, '"short_term"');

const unwrapBracketedArray = (input: string): string => {
  const trimmed = input.trim();
  if (
    trimmed.startsWith("{") &&
    trimmed.endsWith("}") &&
    trimmed.slice(1, -1).trim().startsWith("[") &&
    trimmed.slice(1, -1).trim().endsWith("]")
  ) {
    return trimmed.slice(1, -1).trim();
  }
  return input;
};

const parseTemplateGoals = (input: string): TemplateGoal[] => {
  if (!input || !input.trim()) return [];

  const unwrapped = unwrapBracketedArray(input);
  const normalized = normalizeGoalTypeTokens(unwrapped);
  const parsed = parseWithFallback(normalized);

  if (Array.isArray(parsed)) {
    return parsed as TemplateGoal[];
  }

  if (parsed && typeof parsed === "object") {
    return [parsed as TemplateGoal];
  }

  throw new Error("Template goals must be an object or an array of objects.");
};

const buildQuestionnaireFromValues = (
  values: any,
  parsedTemplateGoals?: TemplateGoal[]
): Questionnaire => {
  const learningPlacementRouting = buildLearningPlacementRouting(values);

  return mergeQuestionnaireWithLearningPlacement(
    values.sourceQuestionnaire || {},
    {
      app: values.app,
      version: values.version,
      categories: parseList(values.categories, ","),
      ai_prompt:
        values.ai_prompt.trim() + "\n\n" + AI_PROMPT_SHARED_TAIL.trim(),
      loading: {
        title: values.loadingTitle,
        progress_label: values.loadingProgressLabel,
        alerts: parseList(values.loadingAlerts, "\n"),
      },
      questions: normalizeQuestions(parseJSONField(values.questionsJson, [])),
      interstitials: parseJSONField(values.interstitialsJson, []),
      beforeAfter: {
        before: parseList(values.beforeAfterBefore, "\n"),
        after: parseList(values.beforeAfterAfter, "\n"),
      },
      commit: {
        greeting: values.commitGreeting,
        commitments: parseList(values.commitCommitments, "\n"),
        subcopy: values.commitSubcopy,
        needSignature:
          String(values.commitNeedSignature).toLowerCase() === "true",
        primaryCta: values.commitPrimaryCta,
      },
      reviews: parseJSONField(values.reviewsJson, []),
      ready: {
        headline: values.readyHeadline,
        bullets: parseList(values.readyBullets, "\n"),
        primaryCta: values.readyPrimaryCta,
      },
      templateGoals:
        parsedTemplateGoals !== undefined
          ? parsedTemplateGoals
          : parseTemplateGoals(values.templateGoalsJson),
    },
    learningPlacementRouting
  ) as Questionnaire;
};

const enhance = withFormik<OuterProps, FormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ item }: OuterProps) => questionnaireToValues(item),
  validationSchema,
  handleSubmit(values, { setSubmitting, props, setStatus }) {
    const {
      onCreateItem,
      onEditItem,
      mode,
      plans = [],
      plansLoading,
      plansError,
    } = props;
    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    const learningPlacementError = validateLearningPlacementSelection({
      values,
      plans,
      plansLoading,
      plansError,
    });
    if (learningPlacementError) {
      setSubmitting(false);
      setStatus?.({ learningPlacementError });
      return;
    }

    let parsedTemplateGoals: TemplateGoal[] = [];

    try {
      parsedTemplateGoals = parseTemplateGoals(values.templateGoalsJson);
      setStatus?.({
        learningPlacementError: null,
        templateGoalsError: null,
      });
    } catch (error) {
      setSubmitting(false);
      setStatus?.({
        templateGoalsError:
          "Invalid Template Goals. Please provide valid JSON or object literal. " +
          (error as Error)?.message,
      });
      return;
    }

    const questionnaire = buildQuestionnaireFromValues(
      values,
      parsedTemplateGoals
    );

    properCallback(questionnaire);
    setSubmitting(false);
  },
  //@ts-ignore
})(OnboardingForm);

export default enhance;
